import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useOpsService, useOpsServiceDomains } from '@/api/operations';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { OpsServiceDetailHeader } from './ops-service-detail-header';
import { OpsCdnConnectionCard } from './ops-cdn-connection-card';
import { OverviewTab } from './tabs/overview-tab';
import { DomainsTab } from './tabs/domains-tab';
import { UsageTab } from './tabs/usage-tab';
import { WaapTab } from './tabs/waap-tab';
import { InstancesTab } from './tabs/instances-tab';
import { StorageTab } from './tabs/storage-tab';

/* ── Tab definitions ─────────────────────────────────────────────────────── */

const TABS = ['overview', 'domains', 'usage', 'waap', 'instances', 'storage'] as const;
type TabValue = (typeof TABS)[number];

const DEFAULT_TAB: TabValue = 'overview';

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Operations service detail page — `/operations/services/:id`.
 * Identity header (Breadcrumbs → name + status pill + category chip) over a
 * URL-persisted tab switcher. Conditional tabs:
 *   - Domains: CDN/WAAP/registrar services only.
 *   - WAAP: Conversant WAAP only (vendor on-demand diff).
 *   - Instances: compute-category services only (DB-only, no live calls).
 *   - Storage: storage-category services only (business summary + analytics).
 * Usage tab is always shown; it dispatches internally by category.
 *
 * CDN/WAAP customer-facing data is now accessed from the org detail page
 * (/operations/orgs/:orgId → CDN / WAAP tabs), not here.
 */
export default function OperationsServiceDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-persisted tab — default to overview when absent or invalid.
  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: TabValue = (TABS as readonly string[]).includes(rawTab)
    ? (rawTab as TabValue)
    : DEFAULT_TAB;

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  // Always mount both hooks so tab switches don't re-trigger the service fetch.
  const service = useOpsService(id);
  const domains = useOpsServiceDomains(id);

  const svcData = service.data;

  // Full-page loading / error guard — mirrors ServiceDetailPage + CustomerDetailPage.
  if (service.isError) {
    return (
      <Container size="full">
        <TabError title="Failed to load service" onRetry={() => service.refetch()} />
      </Container>
    );
  }
  if (service.isLoading || !svcData) {
    return (
      <Container size="full">
        <TabLoading rows={4} />
      </Container>
    );
  }

  const category = svcData.provider?.category ?? '';

  // WAAP tab: Conversant WAAP only — the vendor tab speaks Conversant API.
  const isWaap =
    category === 'waap' &&
    svcData.provider?.legacy_scope === 'External.Conversant.WAAP';

  // Instances tab: compute-category services (DB-only registry, safe to auto-load).
  const isCompute = category === 'compute';

  // Storage tab: storage-category services (Wasabi OSS billing summary).
  const isStorage = category === 'storage';

  // Domains tab: only categories whose services bind domains (cdn/waap/registrar).
  const hasDomains = category === 'cdn' || category === 'waap' || category === 'registrar';

  // A URL tab that this category doesn't render (e.g. ?tab=domains on a
  // compute service) falls back to Overview instead of a blank pane.
  const effectiveTab: TabValue =
    (activeTab === 'domains' && !hasDomains) ||
    (activeTab === 'waap' && !isWaap) ||
    (activeTab === 'instances' && !isCompute) ||
    (activeTab === 'storage' && !isStorage)
      ? 'overview'
      : activeTab;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <OpsServiceDetailHeader data={svcData} onToggled={() => service.refetch()} />

        {/* Console-connection status + connect action (Eagle CDN only; self-gates). */}
        <OpsCdnConnectionCard service={svcData} />

        <Tabs value={effectiveTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {hasDomains && (
              <TabsTrigger value="domains">
                Domains{` (${svcData.domain_count})`}
              </TabsTrigger>
            )}
            <TabsTrigger value="usage">Usage</TabsTrigger>
            {isWaap && <TabsTrigger value="waap">WAAP</TabsTrigger>}
            {isCompute && <TabsTrigger value="instances">Instances</TabsTrigger>}
            {isStorage && <TabsTrigger value="storage">Storage</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab
              data={svcData}
              isLoading={false}
              isError={false}
              onRetry={() => service.refetch()}
            />
          </TabsContent>

          {hasDomains && (
            <TabsContent value="domains">
              <DomainsTab
                data={domains.data}
                isLoading={domains.isLoading}
                isError={domains.isError}
                onRetry={() => domains.refetch()}
              />
            </TabsContent>
          )}

          <TabsContent value="usage">
            {effectiveTab === 'usage' && (
              <UsageTab serviceId={id} category={category} />
            )}
          </TabsContent>

          {isWaap && (
            <TabsContent value="waap">
              {effectiveTab === 'waap' && <WaapTab serviceId={id} />}
            </TabsContent>
          )}

          {isCompute && (
            <TabsContent value="instances">
              {effectiveTab === 'instances' && <InstancesTab serviceId={id} />}
            </TabsContent>
          )}

          {isStorage && (
            <TabsContent value="storage">
              {effectiveTab === 'storage' && <StorageTab serviceId={id} />}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Container>
  );
}
